import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Bot, MessageSquare, LifeBuoy, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const INTENT_LABELS = {
  search: "Ricerca attività/prodotti", delivery_payment_info: "Consegna e pagamenti",
  order_status: "Stato ordine", human_handoff: "Assistenza umana", greeting: "Saluti", unknown: "Non classificato",
};

export default function AssistantAdmin() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [handoffs, setHandoffs] = useState([]);

  const load = () => {
    api.get("/admin/assistant/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/assistant/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/assistant/handoffs").then(({ data }) => setHandoffs(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/admin/assistant/config", { enabled: cfg.enabled, greeting: cfg.greeting }); toast.success("Assistente aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const closeHandoff = async (h) => {
    try { await api.put(`/admin/assistant/handoffs/${h.id}`, { status: "closed" }); toast.success("Richiesta chiusa"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="text-zinc-500 py-16 text-center">Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="assistant-admin">
      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3 flex items-center gap-2"><Bot className="h-4 w-4 text-[#00E676]" /> Assistente clienti</h4>
        <label className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg p-3 mb-3">
          <span className="text-sm">Assistente attivo (visibile ai clienti)</span>
          <Switch data-testid="assistant-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
        </label>
        <div><Label>Messaggio iniziale</Label><Textarea data-testid="assistant-greeting" value={cfg.greeting || ""} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" rows={3} /></div>
        <Button size="sm" data-testid="assistant-save" onClick={save} className="bg-[#00E676] text-black rounded-md mt-3">Salva</Button>
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#00E676]" /> Domande frequenti</h4>
        {stats ? (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(stats.by_intent || {}).map(([k, v]) => (
                <span key={k} className="text-xs rounded-full border border-white/10 bg-[#0A0A0C] px-3 py-1.5">{INTENT_LABELS[k] || k}: <b className="text-[#00E676]">{v}</b></span>
              ))}
            </div>
            <div className="space-y-1.5">
              {(stats.top_questions || []).map((q, i) => (
                <div key={i} data-testid={`assistant-topq-${i}`} className="flex items-center justify-between text-sm bg-[#0A0A0C] border border-white/5 rounded-lg px-3 py-2">
                  <span className="truncate text-zinc-300 flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 text-zinc-500" /> {q.text}</span>
                  <span className="text-zinc-500 text-xs">×{q.count}</span>
                </div>
              ))}
              {(stats.top_questions || []).length === 0 && <div className="text-zinc-500 text-sm py-4 text-center">Ancora nessuna conversazione.</div>}
            </div>
          </>
        ) : <div className="text-zinc-500 text-sm">Caricamento…</div>}
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3 flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-[#00E676]" /> Richieste di assistenza umana</h4>
        <div className="space-y-2">
          {handoffs.map((h) => (
            <div key={h.id} data-testid={`assistant-handoff-${h.id}`} className="bg-[#0A0A0C] border border-white/10 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="min-w-0"><div className="font-medium text-sm">{h.name} <span className={`text-[10px] ml-1 rounded px-1.5 py-0.5 border ${h.status === "open" ? "text-amber-300 border-amber-400/30 bg-amber-400/10" : "text-zinc-400 border-white/10"}`}>{h.status === "open" ? "aperta" : "chiusa"}</span></div><div className="text-xs text-zinc-500 truncate">{h.message}</div></div>
              {h.status === "open" && <Button size="sm" variant="secondary" data-testid={`assistant-close-handoff-${h.id}`} onClick={() => closeHandoff(h)}>Chiudi</Button>}
            </div>
          ))}
          {handoffs.length === 0 && <div className="text-zinc-500 text-sm py-4 text-center">Nessuna richiesta di assistenza.</div>}
        </div>
      </div>
    </div>
  );
}
